"use client";

import { MapPin, PhoneCall, Mail, Clock, ShieldAlert, CircleCheck } from "lucide-react";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const serviceAreas = [{
  name: "Miami & Kendall",
  description: "Comprehensive home inspections and 4-point assessments for residential properties throughout Miami and Kendall.",
  path: "/home-inspection-miami-fl/"
}, {
  name: "Homestead",
  description: "Dedicated inspection services for Homestead homeowners, ensuring safety and insurance compliance for every property.",
  path: "/home-inspection-homestead-fl/"
}, {
  name: "Miami-Dade County",
  description: "Full-scale commercial and residential inspection coverage across the entire Miami-Dade metropolitan area.",
  path: "/home-inspection-miami-dade-county/"
}, {
  name: "Broward County",
  description: "Expert structural evaluations and wind mitigation reports serving Broward County.",
  path: "/home-inspection-broward-county-fl/"
}, {
  name: "Florida Keys",
  description: "Specialized coastal property inspections and structural evaluations throughout the Florida Keys.",
  path: "/home-inspection-florida-keys/"
}];
const contactInfo = {
  phone: "786-382-6895",
  email: "info@trusted-fl.com",
  guarantee: "report delivery is typically within 24 hours when possible"
};
export default function ServiceAreas() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="248027" id="areas-we-serve" className="bg-background text-foreground py-16 md:py-24 overflow-hidden">
      <div ref={ref} className={`container mx-auto px-4 transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        <div className="text-center max-w-3xl mx-auto mb-12 md:mb-16">
          <Badge variant="secondary" className="mb-4 bg-secondary text-secondary-foreground">
            <MapPin className="size-3.5 mr-1" />
            South Florida Coverage
          </Badge>
          <h2 className="text-3xl md:text-5xl font-bold font-default text-foreground mb-4 tracking-tight">
            Areas We Serve
          </h2>
          <p className="text-muted-foreground text-lg font-default">
            Providing certified home inspections, wind mitigation, and 4-point reports across Miami-Dade, Broward, and the Florida Keys.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8 items-start">
          {}
          <div className="lg:col-span-2 grid sm:grid-cols-2 gap-6">
            {serviceAreas.map((area, i) => <Link key={area.name} to={area.path} className="block group">
                <Card data-index={i} className="bg-card border-border group-hover:border-primary/50 transition-all shadow-sm group-hover:shadow-md h-full">
                  <CardHeader className="pb-3">
                    <CardTitle className="flex items-center gap-2 text-xl font-default text-foreground group-hover:text-primary transition-colors">
                      <MapPin className="size-5 text-primary" />
                      {area.name}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-base text-muted-foreground font-default">
                      {area.description}
                    </CardDescription>
                    <div className="mt-4 flex items-center gap-2 text-sm font-medium text-primary">
                      <CircleCheck className="size-4" />
                      <span className="font-default">Certified Local Inspector</span>
                    </div>
                  </CardContent>
                </Card>
              </Link>)}
          </div>

          {}
          <div className="lg:col-span-1">
            <Card className="border-destructive/20 shadow-xl shadow-destructive/5 relative overflow-hidden rounded-lg">
              <div className="absolute top-0 left-0 w-full h-1 bg-destructive" />
              <CardHeader className="bg-destructive/5 pb-6 border-b border-destructive/10">
                <Badge variant="destructive" className="w-fit mb-3 bg-destructive text-destructive-foreground">
                  <ShieldAlert className="size-3.5 mr-1" />
                  Urgent Inspections
                </Badge>
                <CardTitle className="text-2xl font-default text-foreground">
                  Need a Fast Report?
                </CardTitle>
                <CardDescription className="text-base mt-2 text-muted-foreground font-default">
                  Closing deadlines or insurance renewals require immediate attention. We offer priority scheduling.
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-6 flex flex-col gap-6">
                <div className="flex items-start gap-4">
                  <div className="bg-primary/10 p-3 rounded-full shrink-0">
                    <Clock className="size-6 text-primary" />
                  </div>
                  <div>
                    <h4 className="font-semibold font-default text-foreground text-lg">
                      Fast Turnaround
                    </h4>
                    <p className="text-muted-foreground font-default">
                      We guarantee {contactInfo.guarantee} for all standard inspections in our service areas.
                    </p>
                  </div>
                </div>

                <div className="space-y-3 pt-4 border-t border-border">
                  <Button asChild size="lg" className="w-full h-14 text-lg shadow-md bg-primary text-primary-foreground hover:bg-primary/90 font-default">
                    <Link to={`tel:${contactInfo.phone}`}>
                      <PhoneCall className="size-5 mr-2" />
                      Call {contactInfo.phone}
                    </Link>
                  </Button>
                  
                  <Button asChild variant="outline" size="lg" className="w-full h-12 border-primary text-primary hover:bg-primary/5 font-default">
                    <Link to={`mailto:${contactInfo.email}`}>
                      <Mail className="size-4 mr-2" />
                      Email {contactInfo.email}
                    </Link>
                  </Button>

                  <Button asChild size="lg" className="w-full h-12 font-default">
                    <Link to="#schedule-your-inspection">
                      Schedule Your Inspection
                    </Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>;
}